import Color from 'color';
import type {
  IBorderOptions,
  ITableBordersOptions,
  ITableCellOptions,
  ITableOptions,
  ITablePropertiesOptionsBase,
  ITableWidthProperties,
} from 'docx';
import { BorderStyle, Table, TableCell, TableRow } from 'docx';
import { equals, forEachObjIndexed, isEmpty, uniq } from 'ramda';
import type { NodeEntry } from 'slate';

import type { DocumentContext } from './internal';
import { createChangeAttributes, createDocxAlineContentBlock } from './internal';
import type { BaseDocxOptions, TableSuggestions } from './types';
import type {
  PartialTableBorders,
  SuggestedEdit,
  TableBorder,
  TableBorders,
  TableBorderStyle,
  TableCellElement,
  TableElement,
  TableRowElement,
  TableWidth,
} from '../../../slate-api';
import {
  applySuggestions,
  defaultTableCellMargins,
  isInsertTableCellEdit,
  isInsertTableRowEdit,
  isRemoveTableCellEdit,
  isRemoveTableRowEdit,
  isTableElement,
  Queries,
} from '../../../slate-api';
import {
  convertLength,
  inToTwips,
  mapObjectValues,
  mergeWithDefaults,
  ptToEighthPts,
} from '../../../util';
import { toHex } from '../../../util/color';

export function createDocxTable(
  tableEntry: NodeEntry<TableElement>,
  options: { context: DocumentContext }
): Table {
  const [table, tablePath] = tableEntry;
  const { context } = options;

  const { authorIds, date, suggestedProps } = Queries.getSuggestedTableProps(table);
  const applyRevision = !isEmpty(suggestedProps);

  const revision = applyRevision
    ? {
        author: uniq(authorIds)
          .map(id => context.getAuthorName(id))
          .join(', '),
        date,
        id: context.generateEditId(),
        ...createDocxTableProperties(withoutSuggestedEdits(table)),
      }
    : undefined;

  const columnWidths = createDocxColumnWidths(table);
  const columnWidthsRevision = applyRevision
    ? createDocxColumnWidthsRevision(table, { columnWidths, context })
    : undefined;

  const { cellMargin, ...tableProperties } = createDocxTableProperties(table);

  return new Table({
    ...tableProperties,
    columnWidths,
    columnWidthsRevision,
    margins: cellMargin,
    revision,
    rows: table.children.map((row, index) =>
      createDocxTableRow([row, tablePath.concat(index)], { ...options, table })
    ),
  });
}

function withoutSuggestedEdits<TNode extends TableElement | TableCellElement>(node: TNode): TNode {
  return { ...node, suggestedEdits: [] };
}

type DocxTableProperties = Pick<
  ITablePropertiesOptionsBase,
  'alignment' | 'borders' | 'cellMargin' | 'indent' | 'layout' | 'width'
>;

function createDocxTableProperties(table: TableElement): DocxTableProperties {
  const { margins = {}, width = { type: 'auto' } } = applySuggestions(table);

  return {
    alignment: Queries.renderedTableAlign(table),
    borders: createDocxTableBorders(table),
    cellMargin: mapObjectValues(mergeWithDefaults(margins, defaultTableCellMargins), margin =>
      Queries.convertTableMargin(margin, 'tw')
    ),
    indent: createDocxTableIndent(table),
    layout: Queries.renderedTableLayout(table),
    width: convertTableWidthToDocx(width),
  };
}

function createDocxColumnWidths(table: TableElement): number[] {
  const { gridColumns = [] } = applySuggestions(table);
  return gridColumns.map(width => convertLength(width, 'tw'));
}

function createDocxColumnWidthsRevision(
  table: TableElement,
  options: { columnWidths: number[]; context: DocumentContext }
): NonNullable<ITableOptions['columnWidthsRevision']> | undefined {
  const { columnWidths, context } = options;

  const previousColumnWidths = createDocxColumnWidths(withoutSuggestedEdits(table));
  if (!previousColumnWidths.length) return undefined;
  if (equals(previousColumnWidths, columnWidths)) return undefined;

  return { columnWidths: previousColumnWidths, id: context.generateEditId() };
}

function createDocxTableRow(
  tableRowEntry: NodeEntry<TableRowElement>,
  options: { context: DocumentContext; table: TableElement }
): TableRow {
  const { context } = options;
  const [tableRow, tableRowPath] = tableRowEntry;

  const { deletion, insertion } = getSuggestedInsertRemoveTableEdits(
    context,
    tableRow,
    isInsertTableRowEdit,
    isRemoveTableRowEdit
  );

  const children = tableRow.children.map((cell, index) =>
    createDocxTableCell([cell, tableRowPath.concat(index)], {
      ...options,
      tableSuggestions: {
        deletion,
        insertion,
      },
    })
  );

  return new TableRow({
    children,
    deletion,
    insertion,
  });
}

function createDocxTableCell(
  tableCellEntry: NodeEntry<TableCellElement>,
  options: BaseDocxOptions & { tableSuggestions: TableSuggestions }
): TableCell {
  const { context } = options;
  const [tableCell, tableCellPath] = tableCellEntry;

  const { deletion: cellDeletion, insertion: cellInsertion } = getSuggestedInsertRemoveTableEdits(
    context,
    tableCell,
    isInsertTableCellEdit,
    isRemoveTableCellEdit
  );

  const effectiveTableSuggestions: TableSuggestions = {
    deletion: cellDeletion ?? options.tableSuggestions.deletion,
    insertion: cellInsertion ?? options.tableSuggestions.insertion,
  };

  const { authorIds, date, suggestedProps } = Queries.getSuggestedTableCellProps(tableCell);
  const applyRevision = !isEmpty(suggestedProps);

  return new TableCell({
    ...createDocxTableCellProperties(tableCell),
    children: tableCell.children.flatMap((child, index) =>
      createDocxAlineContentBlock([child, tableCellPath.concat(index)], {
        ...options,
        tableSuggestions: effectiveTableSuggestions,
      })
    ),
    deletion: cellDeletion,
    insertion: cellInsertion,
    revision: applyRevision
      ? {
          author: uniq(authorIds)
            .map(id => context.getAuthorName(id))
            .join(', '),
          date,
          id: context.generateEditId(),
          ...createDocxTableCellProperties(withoutSuggestedEdits(tableCell)),
        }
      : undefined,
  });
}

type DocxTableCellProperties = Pick<
  ITableCellOptions,
  'borders' | 'columnSpan' | 'margins' | 'shading' | 'verticalMerge' | 'width'
>;

function createDocxTableCellProperties(tableCell: TableCellElement): DocxTableCellProperties {
  const { margins = {}, width = { type: 'auto' } } = applySuggestions(tableCell);
  const fillColor = Queries.renderedTableCellShade(tableCell)?.fillColor;

  return {
    borders: createDocxTableBorders(tableCell),
    columnSpan: Queries.renderedTableCellColumnSpan(tableCell),
    margins: mapObjectValues(margins, margin => margin && Queries.convertTableMargin(margin, 'tw')),
    shading: { fill: fillColor ? toHex(fillColor) : undefined },
    verticalMerge: Queries.renderedTableCellVerticalMerge(tableCell),
    width: convertTableWidthToDocx(width),
  };
}

function convertTableWidthToDocx(width: number | TableWidth): ITableWidthProperties {
  if (typeof width === 'number') return { size: inToTwips(width), type: 'dxa' };

  switch (width.type) {
    case 'auto':
      return { size: 0, type: 'auto' };
    case 'tw':
      return { size: width.value, type: 'dxa' };
    case 'nil':
      return { size: 0, type: 'nil' };
    case 'pct':
      return { size: width.value * 100, type: 'pct' };
  }
}

const alineBorderToDocxBorder: Record<keyof TableBorders, keyof ITableBordersOptions> = {
  bottom: 'bottom',
  insideH: 'insideHorizontal',
  insideV: 'insideVertical',
  left: 'left',
  right: 'right',
  top: 'top',
};

type NonReadonly<T> = { -readonly [K in keyof T]: T[K] };

function convertBordersToDocxBorders(borders: PartialTableBorders): ITableBordersOptions {
  const result: NonReadonly<ITableBordersOptions> = {};

  forEachObjIndexed((partialBorder, borderKey) => {
    if (!partialBorder) return;
    const border = Queries.mergeTableBorderWithDefault(partialBorder);
    const docxKey = alineBorderToDocxBorder[borderKey];

    result[docxKey] = {
      // Size is in eights of a point.  http://officeopenxml.com/WPtableCellProperties-Borders.php
      size: ptToEighthPts(border.size),
      space: border.space ?? 0,
      style: alineBorderStyleToDocxStyle(border.size, border.style),
      ...createBorderColor(border),
    };
  }, borders);

  return result;
}

function createDocxTableBorders(
  tableOrCell: TableElement | TableCellElement
): ITableBordersOptions {
  /**
   * We use different default borders to Word, so merge the table-level borders with these defaults
   * while leaving cell-level borders as a partial object.
   */
  const borders = isTableElement(tableOrCell)
    ? Queries.mergeTableBordersWithDefaults(tableOrCell)
    : Queries.mergePartialTableBordersWithDefaults(tableOrCell);

  return convertBordersToDocxBorders(borders);
}

// Color is in hex per http://officeopenxml.com/WPtableBorders.php.
function createBorderColor(border: TableBorder): Pick<IBorderOptions, 'color'> {
  if (!border?.color) return {};

  try {
    const borderColor = Color(border.color);
    return {
      color: borderColor.hex(),
    };
  } catch (err) {
    console.error(err.message);
    return {};
  }
}

function alineBorderStyleToDocxStyle(
  size: number,
  alineStyle: TableBorderStyle
): IBorderOptions['style'] {
  // If size === 0, use NIL border style so border doesn't show in export.
  if (!size) return BorderStyle.NIL;

  switch (alineStyle) {
    case 'dashed':
      return BorderStyle.DASHED;
    case 'dotted':
      return BorderStyle.DOTTED;
    case 'double':
      return BorderStyle.DOUBLE;
    case 'groove':
      return BorderStyle.WAVE;
    case 'hidden':
      return BorderStyle.NIL;
    case 'inset':
      return BorderStyle.INSET;
    case 'none':
      return BorderStyle.NIL;
    case 'outset':
      return BorderStyle.OUTSET;
    case 'ridge':
      return BorderStyle.WAVE;
    case 'solid':
      return BorderStyle.SINGLE;
  }
}

function createDocxTableIndent(table: TableElement): ITableWidthProperties | undefined {
  const activeAlign = Queries.renderedTableAlign(table);
  const activeIndent = Queries.renderedTableIndent(table, 'tw');
  if (activeAlign !== 'left' || activeIndent === 0) return undefined;

  return {
    size: activeIndent,
    type: 'dxa',
  };
}

function getSuggestedInsertRemoveTableEdits<
  TElement extends { suggestedEdits?: readonly SuggestedEdit[] | null },
  TInsert extends SuggestedEdit,
  TRemove extends SuggestedEdit,
>(
  context: DocumentContext,
  element: TElement,
  isInsertEdit: (edit: SuggestedEdit) => edit is TInsert,
  isRemoveEdit: (edit: SuggestedEdit) => edit is TRemove
) {
  const { suggestedEdits = [] } = element;
  const insertEdit = suggestedEdits?.find(isInsertEdit);
  const removeEdit = insertEdit ? undefined : suggestedEdits?.find(isRemoveEdit);

  return {
    deletion: removeEdit ? createChangeAttributes(context, removeEdit) : undefined,
    insertion: insertEdit ? createChangeAttributes(context, insertEdit) : undefined,
  };
}
